// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import Breadcrumbs from '~/components/Core/Breadcrumbs';
import { BreadcrumbsContext } from '~/components/layout';
import { useContentStackProps } from '~/contentstack';
import GatsbyBackgroundImage from '../components/images/GatsbyBackgroundImage';
import { withReferencePath } from '../utils/ReferencePaths';
import PageHeroLeftText from './PageHeroLeftText';
import PageHeroLoginForm from './PageHeroLoginForm';
import PageHeroWhiteBox from './PageHeroWhiteBox';

import './PageHero.scss';

const [rootPath, withPaths] = withReferencePath('page_hero');
const Local_REFERENCES = ['background_image', 'mobile_background_image'];
export const PageHero_REFERENCES = [rootPath, ...withPaths(Local_REFERENCES)];

export const query = graphql`
	fragment PageHeroFragment on Ozk_page {
		page_hero {
			uid
			_version
			title
			locale
			has_breadcrumbs
			omit_header_overlay

			cover_content {
				...PageHeroLeftTextFragment
				...PageHeroLoginFormFragment
				...PageHeroWhiteBoxFragment
			}

			mobile_background_image {
				remoteToLocalImage {
					childImageSharp {
						mobile: gatsbyImageData(quality: 90, placeholder: BLURRED, layout: FULL_WIDTH)
					}
				}
				...BynderImageFragment
			}
			background_image {
				remoteToLocalImage {
					childImageSharp {
						desktop: gatsbyImageData(quality: 100, placeholder: BLURRED, layout: FULL_WIDTH)
					}
				}
				...BynderImageFragment
			}
		}
	}
`;

/**
 * @typedef _DesktopGraphQLImage
 * @property {{ childImageSharp: { gatsbyImageData: import('gatsby-plugin-image').IGatsbyImageData } }} remoteToLocalImage
 * @typedef {_DesktopGraphQLImage & import('../CSTypes').BynderImageOptions} DesktopGraphQLImage
 * 
 * @typedef _MobileGraphQLImage
 * @property {{ childImageSharp: { mobile: import('gatsby-plugin-image').IGatsbyImageData } }} remoteToLocalImage
 * @typedef {_MobileGraphQLImage & import('../CSTypes').BynderImageOptions} MobileGraphQLImage
 * 
 * @typedef PageHeroCoverContent
 * @property {import('./PageHeroWhiteBox').PageHeroWhiteBoxOptions} [white_box]
 * @property {import('./PageHeroLoginForm').PageHeroLoginFormOptions} [login_form]
 * @property {import('./PageHeroLeftText').PageHeroLeftTextOptions} [left_text]

 * @typedef PageHeroCS
 * @property {MobileGraphQLImage[]} [mobile_background_image]
 * @property {DesktopGraphQLImage[]} [background_image]
 * @property {PageHeroCoverContent[]} cover_content
 * @property {string} floating_left_text
 * @property {boolean} has_breadcrumbs
 * @property {boolean} omit_header_overlay
 * @property {string} [image_alt_text]
 * @property {string} title CS Model title.
 * @property {string} uid CS unique id.
 * @property {number} _version CS version.
 * @property {string} locale CS locale.
 *
 * @typedef PageHeroProps
 * @property {PageHeroCS} options
 */

/** @type {import('~/types/React').ReactFC<PageHeroProps>} */
const PageHero = ({ options }) => {
	const hasMobileImage = !!options?.mobile_background_image?.[0];

	const [model, modelWith$] = useContentStackProps({
		staticModel: options,
		contentType: 'page_hero',
		contentUid: options?.uid,
		referenceFieldPaths: Local_REFERENCES,
	});

	// If the model has not yet been fetched, just skip.
	// In non-LP, this is a static query built into the code, it will NEVER be empty.
	if (!model?._version) {
		return null;
	}

	return (
		<React.Fragment>
			<div className={hasMobileImage ? 'cs-page-hero personal-mobile-hero' : 'cs-page-hero'}>
				<GatsbyBackgroundImage
					className={classNames(
						'cs-page-hero-bg-image',
						{ 'no-breadcrumbs': model.omit_header_overlay },
						{ 'personal-mobile-image': hasMobileImage }
					)}
					keyOfChildImageSharp="desktop"
					option={model.background_image?.[0]}
					mobileOption={model.mobile_background_image?.[0]}
				>
					<div className="page-hero-content">
						{model.cover_content?.length === 0 ? (
							<div className="no-cover-content" />
						) : (
							modelWith$.cover_content?.map((cont, i) => (
								<React.Fragment key={i}>
									<PageHeroLeftText options={cont.left_text} />
									<PageHeroLoginForm options={cont.login_form} />
									<PageHeroWhiteBox options={cont.white_box} />
								</React.Fragment>
							))
						)}
					</div>
				</GatsbyBackgroundImage>

				{/* Add the breadcrumbs underneath if needed */}
				{model.has_breadcrumbs && (
					<BreadcrumbsContext.Consumer>
						{(value) =>
							!value?.length ? null : (
								<div className="container bread-crumb">
									<Breadcrumbs paths={value} />
								</div>
							)
						}
					</BreadcrumbsContext.Consumer>
				)}
			</div>
		</React.Fragment>
	);
};

export default PageHero;
export { Head } from '~/components/Modules/LoginCardMain';
